/**
 * HomePage — post-login welcome.
 * Greeting plus four destinations, each with a reason to open it.
 */

import React from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
    TrendingUp,
    AccountBalance,
    PieChart,
    GridView,
} from '@mui/icons-material';
import headerLogo from '../assets/branding/Header_logo.png';
import '../styles/HomePage.css';

const destinations = [
    {
        key: 'stocks',
        title: 'Stocks Research',
        subtitle: 'Company research + notes',
        description: 'Open a company when you want the full research file: statements, ratio trends, valuation, forensic screens, AI answers, and your own notes on Indian listed names.',
        icon: TrendingUp,
        route: '/dashboard',
        accent: 'var(--brand-2)',
    },
    {
        key: 'funds',
        title: 'Mutual Funds / Indices',
        subtitle: 'NAV tracking + indices + analytics',
        description: 'Come here to read the market first: index levels, mutual fund rolling returns, and risk against the benchmark (Sharpe, beta, alpha) before you pick a fund or an index.',
        icon: AccountBalance,
        route: '/funds',
        accent: 'var(--brand)',
    },
    {
        key: 'portfolio',
        title: 'Live Portfolio',
        subtitle: 'Broker sync + XIRR + news + allocation',
        description: 'Use this when the question is your own money: sync broker holdings, see money-weighted return (XIRR), watch allocation drift, and read the day’s portfolio news.',
        icon: PieChart,
        route: '/portfolio',
        accent: 'var(--chart-series-3)',
    },
    {
        key: 'dashboards',
        title: 'Dashboards',
        subtitle: 'Macro · geopolitical · market events',
        description: 'Use these when a live event is moving markets: Hormuz and energy, the India data-centre buildout, and a side-by-side look at Indian banks.',
        icon: GridView,
        route: '/dashboards',
        accent: 'var(--warning)',
    },
];

const HomePage = () => {
    const navigate = useNavigate();
    const context = useOutletContext() || {};
    const isDarkMode = context.isDarkMode ?? localStorage.getItem('theme') === 'dark';
    const toggleTheme = context.toggleTheme ?? (() => { });
    const handleMenuOpen = context.handleMenuOpen ?? (() => { });
    const getUserInitials = context.getUserInitials ?? (() => 'U');
    const getDisplayName = context.getDisplayName ?? (() => 'User');
    const getProfilePhotoUrl = context.getProfilePhotoUrl ?? (() => '');
    const getProfilePhotoLoading = context.getProfilePhotoLoading ?? (() => false);
    const profilePhotoUrl = getProfilePhotoUrl();
    const profilePhotoLoading = getProfilePhotoLoading();
    const displayName = getDisplayName();

    const openDestination = (route) => {
        navigate(route);
    };

    const onDestinationKeyDown = (event, route) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openDestination(route);
        }
    };

    return (
        <div className="homepage">
            <header className="unified-header">
                <div className="header-brand" onClick={() => navigate('/home')}>
                    <img src={headerLogo} alt="Finlytics Forge" className="header-logo-svg" />
                    <span className="brand__text">
                        <span className="brand__title">Finlytics</span>
                        <span className="brand__subtitle">Forge</span>
                    </span>
                </div>

                <div className="header-tagline">
                    Financial Research &amp; Analytics Platform
                </div>

                <div className="header-actions">
                    <button
                        type="button"
                        className="action-btn theme-btn"
                        onClick={toggleTheme}
                        title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
                    >
                        {isDarkMode ? '☀️' : '🌙'}
                    </button>
                    <div className="user-profile" onClick={handleMenuOpen}>
                        <span className="user-name">{displayName}</span>
                        <div className="user-avatar">
                            {profilePhotoLoading ? (
                                <div className="user-avatar-shimmer" />
                            ) : profilePhotoUrl ? (
                                <img src={profilePhotoUrl} alt={displayName} className="user-avatar-img" />
                            ) : (
                                getUserInitials()
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <main className="homepage-main">
                <div className="homepage-shell">
                    <section className="homepage-hero" aria-label="Welcome">
                        <div className="grand-welcome-wrap">
                            <div className="grand-welcome-avatar">
                                {profilePhotoLoading ? (
                                    <div className="user-avatar-shimmer" />
                                ) : profilePhotoUrl ? (
                                    <img src={profilePhotoUrl} alt="" className="grand-welcome-avatar-img" />
                                ) : (
                                    <span>{getUserInitials()}</span>
                                )}
                            </div>
                            <p className="grand-welcome-text">Grand welcome, {displayName}</p>
                        </div>
                        <h1 className="homepage-title">Welcome to Finlytics Forge</h1>
                        <p className="homepage-subtitle">Choose your research destination</p>
                        <p className="homepage-helper">
                            Each path below is a different job. Pick the one that matches the question you came in to answer.
                        </p>
                    </section>

                    <section className="homepage-cards" aria-label="Research destinations">
                        {destinations.map((destination) => {
                            const Icon = destination.icon;
                            return (
                                <div
                                    key={destination.key}
                                    className="homepage-card"
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`${destination.title}. ${destination.description}`}
                                    onClick={() => openDestination(destination.route)}
                                    onKeyDown={(event) => onDestinationKeyDown(event, destination.route)}
                                    style={{ '--card-accent': destination.accent }}
                                >
                                    <div className="homepage-card-icon" aria-hidden="true">
                                        <Icon style={{ fontSize: 28, color: destination.accent }} />
                                    </div>
                                    <h2 className="homepage-card-title">{destination.title}</h2>
                                    <p className="homepage-card-subtitle">{destination.subtitle}</p>
                                    <p className="homepage-card-desc">{destination.description}</p>
                                    <div className="homepage-card-arrow" aria-hidden="true">→</div>
                                </div>
                            );
                        })}
                    </section>
                </div>
            </main>
        </div>
    );
};

export default HomePage;
